import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Globe2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useT } from "@/i18n/LanguageContext";
import LanguageSwitcher from "@/components/landing/LanguageSwitcher";

export default function Navbar() {
  const { t } = useT();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const NAV_LINKS = [
    { label: t.nav.about, href: "#about" },
    { label: t.nav.services, href: "#services" },
    { label: t.nav.products, href: "#products" },
    { label: t.nav.process, href: "#process" },
    { label: t.nav.partners, href: "#partners" },
    { label: t.nav.testimonials, href: "#testimonials" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/75 backdrop-blur-xl border-b border-slate-200/70 shadow-[0_1px_0_rgba(15,23,42,0.04)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        {/* Brand */}
        <a
          href="#hero"
          data-testid="brand-logo"
          className="flex items-center gap-2.5 group"
        >
          <div className="relative w-9 h-9 rounded-lg bg-[#005BEA] flex items-center justify-center shadow-[0_4px_18px_rgba(0,91,234,0.35)]">
            <Globe2 className="w-5 h-5 text-white" strokeWidth={2.4} />
            <div className="absolute -bottom-1 -right-1 w-2.5 h-2.5 rounded-full bg-[#FF6B00] border-2 border-white" />
          </div>
          <div className="leading-none">
            <div className={`font-display font-bold tracking-tight text-[17px] ${
              scrolled ? "text-slate-900" : "text-white"
            }`}>
              klairys
            </div>
            <div className={`text-[10px] tracking-[0.18em] uppercase font-medium mt-0.5 ${
              scrolled ? "text-slate-500" : "text-white/70"
            }`}>
              .fr
            </div>
          </div>
        </a>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-1" aria-label={t.nav.ariaMain}>
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={`nav-link-${link.href.slice(1)}`}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                scrolled
                  ? "text-slate-700 hover:text-[#005BEA] hover:bg-slate-100"
                  : "text-white/85 hover:text-white hover:bg-white/10"
              }`}
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <LanguageSwitcher scrolled={scrolled} />
          <Button
            data-testid="navbar-cta-contact"
            asChild
            className="hidden sm:inline-flex bg-[#005BEA] hover:bg-[#0048b9] text-white rounded-full px-5 h-10 font-medium shadow-[0_6px_20px_rgba(0,91,234,0.28)] transition-all"
          >
            <a href="#contact">{t.nav.cta}</a>
          </Button>
          <button
            data-testid="mobile-menu-toggle"
            aria-label={t.nav.openMenu}
            onClick={() => setOpen((v) => !v)}
            className={`lg:hidden p-2 rounded-md transition-colors ${
              scrolled
                ? "text-slate-900 hover:bg-slate-100"
                : "text-white hover:bg-white/10"
            }`}
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden overflow-hidden bg-white border-t border-slate-200"
          >
            <div className="px-6 py-5 flex flex-col gap-1">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setOpen(false)}
                  className="px-3 py-3 text-slate-700 font-medium rounded-md hover:bg-slate-100"
                >
                  {link.label}
                </a>
              ))}
              <a
                href="#contact"
                onClick={() => setOpen(false)}
                className="mt-2 px-3 py-3 text-center bg-[#005BEA] text-white font-medium rounded-md"
              >
                {t.nav.cta}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
